import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";

export interface Day {
  date: string;
  label: string;
}

export interface WeeklyPracticeChartProps {
  weekLabel: string;
  weekDays: Day[];
  dailyDistances: Record<string, number[]>;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  formatNumber?: (n: number | string) => string;
}

export function WeeklyPracticeChart({
  weekLabel,
  weekDays,
  dailyDistances,
  onPreviousWeek,
  onNextWeek,
  formatNumber = (n) =>
    typeof n === "number" ? n.toLocaleString() : n,
}: WeeklyPracticeChartProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent Practices</CardTitle>
          <CardDescription>Weekly overview</CardDescription>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={onPreviousWeek}>
            <span className="sr-only">Previous Week</span>
            <svg viewBox="0 0 24 24" className="h-4 w-4">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Button>

          <Button variant="outline" size="icon" onClick={onNextWeek}>
            <span className="sr-only">Next Week</span>
            <svg viewBox="0 0 24 24" className="h-4 w-4">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="text-sm text-muted-foreground mb-4 text-center">
          Showing week of {weekLabel}
        </div>

        <div className="w-full h-56 bg-primary/85 rounded-md">
          <div className="flex items-end justify-between h-full px-4 pb-4">
            {weekDays.map((day) => {
              const distances = dailyDistances[day.label] ?? [];
              const hiddenCount = Math.max(0, distances.length - 10);
              const visibleDistances = distances.slice(-10);

              return (
                <div
                  key={day.label}
                  className="flex flex-col items-center justify-end min-w-[56px]"
                >
                  <div
                    className="
                      mb-2 flex flex-col-reverse items-center gap-1 max-h-40 overflow-y-auto overflow-x-hidden pr-1
                      [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.55)_transparent]
                      [&::-webkit-scrollbar]:w-1
                      [&::-webkit-scrollbar-track]:bg-transparent
                      [&::-webkit-scrollbar-thumb]:rounded-full
                      [&::-webkit-scrollbar-thumb]:bg-white/60
                    "
                  >
                    {visibleDistances.map((d, i) => (
                      <div
                        key={i}
                        className="min-w-[44px] px-2 py-0.5 bg-white text-black text-[11px] text-center rounded whitespace-nowrap"
                        title={String(d)}
                      >
                        {formatNumber(d)}
                      </div>
                    ))}
                  </div>
                  {hiddenCount > 0 && (
                    <div className="text-[10px] text-white/90 mb-1">
                      +{hiddenCount} more
                    </div>
                  )}

                  <div className="text-white text-xs">{day.date}</div>
                  <div className="text-white font-medium">{day.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}